import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { ThirdPartiesService } from '../../../services/third-parties.service';
import { ProjectService } from '../../../services/project.service';
import { ApplicationUsersService } from '../../../services/application-users.service';
import { WorkOrdersService } from '../../../services/work-orders.service';
import { ToastService } from '../../../services/toast.service';
import { debounceTime, forkJoin } from 'rxjs';
import { LoadingService } from '../../../services/loading.service';
import { Router, ActivatedRoute } from '@angular/router';
import { FeeTypesService } from '../../../services/fee-types.service';
import { BaseFormComponent } from '../../shared/base-form/base-form.component';
import { ConfirmationService } from '../../../services/confirmation.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-new-work-order',
  templateUrl: './new-work-order.component.html',
  styleUrl: './new-work-order.component.scss'
})
export class NewWorkOrderComponent extends BaseFormComponent implements OnInit {

  workOrderForm!: FormGroup;
  workOrderItemsFormArray!: FormArray;
  public thirdParties: any;
  public applicationUsersData: any = [];
  public projects: any = []
  feeTypes: any = [];
  public totalExecutedAmount: number = 0;
  projectCategories: any[][] = []; // Un array de arrays, uno por cada item
  private budgetAlertShown: boolean = false; // Para evitar múltiples alertas

  // Variables para edición
  isEditMode: boolean = false;
  workOrderId: number | null = null;
  currentWorkOrder: any = null;



  constructor(
    private fb: FormBuilder,
    router: Router,
    private route: ActivatedRoute,
    private _thirdPartiesService: ThirdPartiesService,
    private _projectService: ProjectService,
    private _applicationUsers: ApplicationUsersService,
    private _workOrdersService: WorkOrdersService,
    private toastService: ToastService,
    private _loadingService: LoadingService,
    private _feeTypesService: FeeTypesService,
    confirmationService: ConfirmationService
  ) {
    super(confirmationService, router);
  }

  // === MÉTODOS REQUERIDOS POR BaseFormComponent ===

  protected override initializeForm(): void {
    // El formulario se inicializa en ngOnInit
  }

  protected override getMainForm(): FormGroup {
    return this.workOrderForm;
  }

  public override getFormName(): string {
    return 'orden de trabajo';
  }

  public override getCriticalData(): string[] {
    const data: string[] = [];

    // Información del tercero
    const thirdPartyId = this.workOrderForm.get('thirdPartyId')?.value;
    const thirdParty = this.thirdParties?.find((t: any) => t.id === thirdPartyId);
    if (thirdParty) {
      data.push(`Tercero: ${thirdParty.name}`);
    }

    // Información del proyecto
    const projectId = this.workOrderForm.get('projectId')?.value;
    const project = this.projects?.find((p: any) => p.id === projectId);
    if (project) {
      data.push(`Proyecto: ${project.projectId} - ${project.name}`);
    }

    // Monto total
    data.push(`Total: ${this.totalExecutedAmount.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}`);

    // Número de elementos
    const itemsCount = this.workOrderItems.length;
    if (itemsCount > 0) {
      data.push(`Elementos: ${itemsCount}`);
    }

    return data;
  }

  protected override async performFormSubmit(): Promise<void> {
    await this.submitWorkOrder();
  }

  protected override navigateBack(): void {
    this.router.navigate(['/work-orders']);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Verificar si estamos en modo edición o duplicación
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.workOrderId = +params['id'];
        this.loadWorkOrderForEdit();
      } else {
        this.isEditMode = false;
        this.route.queryParams.subscribe(queryParams => {
          if (queryParams['duplicateId']) {
            this.loadWorkOrderForDuplicate(+queryParams['duplicateId']);
          } else {
            this.loadInitialData();
          }
        });
      }
    });

    this.workOrderForm = this.fb.group({
      thirdPartyId: [null, Validators.required],
      payerId: [null, Validators.required],
      status: [1, Validators.required],
      paymentDate: [Date.now, Validators.required],
      workOrderItems: this.fb.array([this.createWorkOrderItem()]),
      feeDetails: this.fb.array([])
    });

    this.setupExistingListeners();
  }

  loadInitialData() {
    this._loadingService.show();
    forkJoin({
      thirdParties: this._thirdPartiesService.getAll(),
      projects: this._projectService.getAll(),
      applicationUsers: this._applicationUsers.getAll(),
      feeTypes: this._feeTypesService.getAll()
    }).subscribe({
      next: (results) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const thirdPartiesData = Array.isArray(results.thirdParties.data) ? results.thirdParties.data : (results.thirdParties.data?.data || []);
        const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
        // feeTypes siempre devuelve un array directamente en data
        const feeTypesData = results.feeTypes.data || [];

        this.thirdParties = thirdPartiesData.map((data: any) => ({ label: `${data.documentId} - ${data.firstName} ${data.lastName}`, value: data.id, data }));
        this.projects = projectsData.map((data: any) => ({ label: `${data.projectId} ${data.name}`, value: data.id, data }));
        this.applicationUsersData = results.applicationUsers.map((data: any) => ({ label: `${data.firstName} ${data.lastName}`, value: data.id, data }));
        this.feeTypes = feeTypesData;
        this._loadingService.hide();
      },
      error: (err) => {
        console.error("Error loading data", err);
        this._loadingService.hide();
      }
    });
  }

  // 🔍 Búsqueda server-side para terceros
  onSearchThirdParties(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Recargar datos iniciales
      this._thirdPartiesService.getAll().subscribe({
        next: (results) => {
          const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.thirdParties = data.map((d: any) => ({
            label: `${d.documentId} - ${d.firstName} ${d.lastName}`,
            value: d.id,
            data: d
          }));
        },
        error: (err) => console.error('Error loading third parties:', err)
      });
      return;
    }

    this._thirdPartiesService.getAllFiltered(query).subscribe({
      next: (results) => {
        const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.thirdParties = data.map((d: any) => ({
          label: `${d.documentId} - ${d.firstName} ${d.lastName}`,
          value: d.id,
          data: d
        }));
      },
      error: (err) => console.error('Error searching third parties:', err)
    });
  }

  // 🔍 Búsqueda server-side para proyectos
  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      // Recargar datos iniciales
      this._projectService.getAll().subscribe({
        next: (results) => {
          const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
          this.projects = data.map((d: any) => ({
            label: `${d.projectId} ${d.name}`,
            value: d.id,
            data: d
          }));
        },
        error: (err) => console.error('Error loading projects:', err)
      });
      return;
    }

    this._projectService.getAllFiltered(query).subscribe({
      next: (results) => {
        const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
        this.projects = data.map((d: any) => ({
          label: `${d.projectId} ${d.name}`,
          value: d.id,
          data: d
        }));
      },
      error: (err) => console.error('Error searching projects:', err)
    });
  }

  loadWorkOrderForEdit() {
    if (!this.workOrderId) return;

    this._loadingService.show();
    forkJoin({
      thirdParties: this._thirdPartiesService.getAll(),
      projects: this._projectService.getAll(),
      applicationUsers: this._applicationUsers.getAll(),
      feeTypes: this._feeTypesService.getAll(),
      workOrder: this._workOrdersService.getById(this.workOrderId)
    }).subscribe({
      next: (results) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const thirdPartiesData = Array.isArray(results.thirdParties.data) ? results.thirdParties.data : (results.thirdParties.data?.data || []);
        const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
        // feeTypes siempre devuelve un array directamente en data
        const feeTypesData = results.feeTypes.data || [];

        this.thirdParties = thirdPartiesData.map((data: any) => ({ label: `${data.documentId} - ${data.firstName} ${data.lastName}`, value: data.id, data }));
        this.projects = projectsData.map((data: any) => ({ label: `${data.projectId} ${data.name}`, value: data.id, data }));
        this.applicationUsersData = results.applicationUsers.map((data: any) => ({ label: `${data.firstName} ${data.lastName}`, value: data.id, data }));
        this.feeTypes = feeTypesData;

        this.currentWorkOrder = results.workOrder.data;

        // 🔧 Asegurar que el tercero de la orden actual esté en las opciones del dropdown
        if (this.currentWorkOrder.thirdParty) {
          const tp = this.currentWorkOrder.thirdParty;
          const existsInList = this.thirdParties.some((t: any) => t.value === tp.id);
          if (!existsInList) {
            this.thirdParties.unshift({
              label: `${tp.documentId} - ${tp.firstName || ''} ${tp.lastName || ''} ${tp.commercialName || ''}`.trim(),
              value: tp.id,
              data: tp
            });
          }
        }

        // Verificar si la orden puede ser editada
        if (this.currentWorkOrder.status === 'PAGADA') {
          this.toastService.showToast('error', 'No se puede editar una orden de trabajo que ya está pagada');
          this.router.navigate(['/work-orders']);
          return;
        }

        if (this.currentWorkOrder.isCancelled) {
          this.toastService.showToast('error', 'No se puede editar una orden de trabajo anulada');
          this.router.navigate(['/work-orders']);
          return;
        }

        // Poblar el formulario después de que todos los datos estén cargados
        setTimeout(() => {
          this.populateFormWithWorkOrder();
        }, 100);
        this._loadingService.hide();
      },
      error: (err) => {
        console.error("Error loading work order for edit", err);
        this.toastService.showToast('error', 'Error al cargar la orden de trabajo');
        this.router.navigate(['/work-orders']);
        this._loadingService.hide();
      }
    });
  }

  loadWorkOrderForDuplicate(id: number) {
    this._loadingService.show();
    forkJoin({
      thirdParties: this._thirdPartiesService.getAll(),
      projects: this._projectService.getAll(),
      applicationUsers: this._applicationUsers.getAll(),
      feeTypes: this._feeTypesService.getAll(),
      workOrder: this._workOrdersService.getById(id)
    }).subscribe({
      next: (results) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const thirdPartiesData = Array.isArray(results.thirdParties.data) ? results.thirdParties.data : (results.thirdParties.data?.data || []);
        const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
        // feeTypes siempre devuelve un array directamente en data
        const feeTypesData = results.feeTypes.data || [];

        this.thirdParties = thirdPartiesData.map((data: any) => ({ label: `${data.documentId} - ${data.firstName} ${data.lastName}`, value: data.id, data }));
        this.projects = projectsData.map((data: any) => ({ label: `${data.projectId} ${data.name}`, value: data.id, data }));
        this.applicationUsersData = results.applicationUsers.map((data: any) => ({ label: `${data.firstName} ${data.lastName}`, value: data.id, data }));
        this.feeTypes = feeTypesData;

        this.currentWorkOrder = results.workOrder.data;

        // 🔧 Asegurar que el tercero de la orden actual esté en las opciones del dropdown
        if (this.currentWorkOrder.thirdParty) {
          const tp = this.currentWorkOrder.thirdParty;
          const existsInList = this.thirdParties.some((t: any) => t.value === tp.id);
          if (!existsInList) {
            this.thirdParties.unshift({
              label: `${tp.documentId} - ${tp.firstName || ''} ${tp.lastName || ''} ${tp.commercialName || ''}`.trim(),
              value: tp.id,
              data: tp
            });
          }
        }

        // Poblar el formulario después de que todos los datos estén cargados
        setTimeout(() => {
          this.populateFormWithWorkOrder();
          // Resetear fecha a hoy y estado a PENDIENTE para duplicación
          this.workOrderForm.patchValue({
            paymentDate: new Date().toISOString().split('T')[0],
            status: 1 // PENDIENTE
          });
        }, 100);
        this._loadingService.hide();
      },
      error: (err) => {
        console.error("Error loading work order for duplicate", err);
        this.toastService.showToast('error', 'Error al cargar la orden de trabajo para duplicar');
        this.loadInitialData(); // Fallback a carga inicial limpia
      }
    });
  }

  populateFormWithWorkOrder() {
    if (!this.currentWorkOrder) return;


    // Limpiar el FormArray de workOrderItems
    while (this.workOrderItems.length !== 0) {
      this.workOrderItems.removeAt(0);
    }

    // Limpiar el FormArray de feeDetails
    while (this.feeDetails.length !== 0) {
      this.feeDetails.removeAt(0);
    }

    // Poblar los campos principales
    this.workOrderForm.patchValue({
      thirdPartyId: this.currentWorkOrder.thirdPartyId,
      payerId: this.currentWorkOrder.payerId,
      status: this.currentWorkOrder.status === 'PENDIENTE' ? 1 : this.currentWorkOrder.status === 'PAGADA' ? 3 : 2,
      paymentDate: new Date(this.currentWorkOrder.paymentDate).toISOString().split('T')[0]
    });

    // Poblar workOrderItems
    this.currentWorkOrder.workOrderItems.forEach((item: any, index: number) => {
      const itemGroup = this.createWorkOrderItem();

      // Encontrar el proyecto que contiene este projectBudgetId
      console.log('DEBUG: Buscando proyecto para projectBudgetId:', item.projectBudgetId);
      console.log('DEBUG: Proyectos disponibles:', this.projects);

      const project = this.projects.find((p: any) =>
        p.data.budgets.some((b: any) => b.id === item.projectBudgetId)
      );

      console.log('DEBUG: Proyecto encontrado:', project);

      // Poblar el formulario con los datos del item
      itemGroup.patchValue({
        projectId: project ? project.value : null, // Establecer el ID del proyecto
        projectBudgetId: item.projectBudgetId,
        details: item.details,
        projectedAmount: item.projectedAmount,
        unitValue: item.unitValue,
        qty: item.qty,
        vatAmount: item.vatAmount,
        executedAmount: item.executedAmount
      });

      this.workOrderItems.push(itemGroup);
      this.projectCategories.push([]);
      this.setupItemListeners(itemGroup);

      // Cargar las categorías del proyecto para este item
      if (project) {
        const filteredBudgets = project.data.budgets.filter(
          (budget: any) => budget.budgetSubcategory.budgetCategory.budgetType === 'EXPENSE'
        );
        this.projectCategories[index] = filteredBudgets.map((data: any) => ({
          label: `${data.budgetSubcategory?.budgetCategory.name} ${data.budgetSubcategory?.name}`,
          value: data.id,
          data
        }));

        console.log('DEBUG: Categorías cargadas para el proyecto:', this.projectCategories[index]);
      } else {
        console.log('DEBUG: No se encontró proyecto para projectBudgetId:', item.projectBudgetId);
      }
    });

    // Poblar feeDetails si existen
    if (this.currentWorkOrder.feeDetails && this.currentWorkOrder.feeDetails.length > 0) {
      this.currentWorkOrder.feeDetails.forEach((fee: any) => {
        const feeGroup = this.fb.group({
          feeTypeId: [fee.feeTypeId, Validators.required],
          requestAmount: [fee.requestAmount, [Validators.required, Validators.min(1)]],
          description: [fee.description || '']
        });
        this.feeDetails.push(feeGroup);
      });
    }

    this.calculateTotalExecutedAmount();

  }

  getCategoryByProject(event: any, index: number): void {
    const id = event.value;
    const project = this.projects.find((p: any) => p.data.id == id);
    console.log("mostrando project", project);
    if (project) {
      const filteredBudgets = project.data.budgets.filter(
        (budget: any) => budget.budgetSubcategory.budgetCategory.budgetType === 'EXPENSE'
      );
      this.projectCategories[index] = filteredBudgets.map((data: any) => ({ label: `${data.budgetSubcategory?.budgetCategory.name} ${data.budgetSubcategory?.name}`, value: data.id, data }));
    }
  }

  // Crear nuevo FormGroup para workOrderItems
  createWorkOrderItem(): FormGroup {
    return this.fb.group({
      projectId: [null, Validators.required], // Nuevo campo para el proyecto
      projectBudgetId: [0, Validators.required],
      details: ['', Validators.required],
      projectedAmount: [0, Validators.required],
      unitValue: [null, Validators.required],
      qty: [null, Validators.required],
      vatAmount: [null, Validators.required],
      executedAmount: ['', Validators.required]
    });
  }

  // Getter para obtener el FormArray de workOrderItems
  get workOrderItems(): FormArray {
    return this.workOrderForm.get('workOrderItems') as FormArray;
  }

  private setupExistingListeners(): void {
    this.workOrderItems.controls.forEach((item: any) => {
      this.setupItemListeners(item);
    });
  }


  private calculateTotalExecutedAmount(): void {
    const items = this.workOrderItems.controls;
    this.totalExecutedAmount = items.reduce((total, item) => {
      const value = Number(this.parseCurrencyValue(item.get('executedAmount')?.value)) || 0;
      return total + value;
    }, 0);
  }


  private setupItemListeners(formGroup: FormGroup): void {
    const controls = ['unitValue', 'qty', 'vatAmount'];

    controls.forEach(controlName => {
      formGroup.get(controlName)?.valueChanges.pipe(
        debounceTime(300)
      ).subscribe(() => {
        this.calculateExecutedAmount(formGroup);
        this.calculateTotalExecutedAmount();
      });
    });
  }

  private calculateExecutedAmount(formGroup: FormGroup): void {
    const unitValue = Number(formGroup.get('unitValue')?.value) || 0;
    const qty = Number(formGroup.get('qty')?.value) || 0;
    const vatAmount = Number(formGroup.get('vatAmount')?.value) || 0;

    const executedAmount = (unitValue * qty) + vatAmount;
    const projectedAmount = Number(formGroup.get('projectedAmount')?.value) || 0;

    formGroup.get('executedAmount')?.patchValue(executedAmount, { emitEvent: true });

    // Verificar si el monto ejecutado supera el proyectado
    if (executedAmount > projectedAmount && projectedAmount > 0) {
      this.showBudgetExceededAlert(executedAmount, projectedAmount);
    }
  }

  // Agregar nuevo ítem de orden de trabajo
  addWorkOrderItem(): void {
    const newItem = this.createWorkOrderItem();
    this.workOrderItems.push(newItem);
    this.projectCategories.push([]);
    this.setupItemListeners(newItem);
  }

  // Eliminar un ítem de orden de trabajo
  removeWorkOrderItem(index: number): void {
    if (this.workOrderItems.length > 1) {
      this.workOrderItems.removeAt(index);
      this.projectCategories.splice(index, 1);
      this.calculateTotalExecutedAmount();
    }
  }

  loadProjectedAmount(data: any, index: number) {
    const subcategoryId = data.value; // Para PrimeNG dropdown

    // Buscar la subcategoría en el array de categorías para este índice específico
    const categoryItem = this.projectCategories[index]?.find((item: any) => item.value === subcategoryId);

    if (categoryItem && categoryItem.data) {
      let itemsArray = this.workOrderForm.get('workOrderItems') as FormArray;
      let itemGroup = itemsArray.at(index) as FormGroup;
      itemGroup.get('projectedAmount')?.setValue(categoryItem.data.projectedAmount);
    }
  }


  get feeDetails(): FormArray {
    return this.workOrderForm?.get('feeDetails') as FormArray || this.fb.array([]);
  }

  addFeeDetail() {
    const feeGroup = this.fb.group({
      feeTypeId: ['', Validators.required],
      requestAmount: ['', [Validators.required, Validators.min(1)]],
      description: ['']
    });

    this.feeDetails.push(feeGroup);
  }

  // Eliminar un Fee
  removeFeeDetail(index: number) {
    this.feeDetails.removeAt(index);
  }


  calculateVAT(index: number, event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;

    if (inputValue.includes('%')) {
      const percentageMatch = inputValue.match(/(\d+\.?\d*)%/);
      if (!percentageMatch) return;

      const percentage = parseFloat(percentageMatch[1]);
      const itemGroup = this.workOrderItems.at(index) as FormGroup;

      const unitValue = this.parseCurrencyValue(itemGroup.get('unitValue')?.value || 0);
      const qty = parseFloat(itemGroup.get('qty')?.value || 0);

      if (!isNaN(unitValue) && !isNaN(qty)) {
        const vatAmount = (unitValue * qty) * (percentage / 100);

        // Actualizar el control usando patchValue para evitar ciclos infinitos
        itemGroup.patchValue({
          vatAmount: vatAmount.toFixed(2),
          executedAmount: (unitValue * qty + vatAmount).toFixed(2)
        }, { emitEvent: false });
      }

      // Forzar actualización del formato
      setTimeout(() => {
        inputElement.value = itemGroup.get('vatAmount')?.value;
      });
    }
  }

  private parseCurrencyValue(value: string | number): number {
    if (typeof value === 'string') {
      return parseFloat(value.replace(/[^0-9.]/g, ''));
    }
    return value;
  }

  // Función que se ejecuta al enviar el formulario
  async onSubmit(): Promise<void> {
    await this.handleFormSubmit();
  }

  private async submitWorkOrder(): Promise<void> {
    if (this.workOrderForm.valid) {
      this._loadingService.show();
      const workOrderData = {
        ...this.workOrderForm.value,
        thirdPartyId: Number(this.workOrderForm.value.thirdPartyId),
        workOrderItems: this.workOrderForm.value.workOrderItems.map((data: any, index: number) => {
          const { projectId, ...itemData } = data; // Excluir projectId del envío
          return {
            ...itemData,
            projectBudgetId: Number((this.workOrderItems.at(index) as FormGroup).get('projectBudgetId')?.value)
          };
        })
      };

      const serviceCall = this.isEditMode && this.workOrderId
        ? this._workOrdersService.update(this.workOrderId, workOrderData)
        : this._workOrdersService.insert(workOrderData);

      serviceCall.subscribe({
        next: (response: any) => {
          const message = this.isEditMode
            ? 'Orden de trabajo actualizada exitosamente.'
            : 'Orden de trabajo creada exitosamente.';
          this.handleSuccess(message);
        },
        error: (err) => {
          const message = this.isEditMode
            ? 'Error al actualizar la orden de trabajo.'
            : 'Error al crear la orden de trabajo.';
          this.handleError(message);
        }
      })

    } else {
      const message = this.isEditMode
        ? 'Error al actualizar la orden de trabajo - Formulario inválido'
        : 'Error al crear la orden de trabajo - Formulario inválido';
      this.handleError(message);
    }
  }

  private handleSuccess(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('success', message);
    this.router.navigate(['/work-orders']);
  }

  private handleError(message: string): void {
    this._loadingService.hide();
    this.toastService.showToast('error', message);
  }

  // === MÉTODOS DE CANCELACIÓN Y NAVEGACIÓN ===

  /**
   * Maneja el botón cancelar con confirmación
   */
  async onCancel(): Promise<void> {
    await this.handleFormCancel('/work-orders');
  }

  /**
   * Muestra alerta cuando el monto ejecutado supera el proyectado
   */
  private async showBudgetExceededAlert(executedAmount: number, projectedAmount: number): Promise<void> {
    // Evitar mostrar múltiples alertas consecutivas
    if (this.budgetAlertShown || this.showConfirmationModal) {
      return;
    }

    const difference = executedAmount - projectedAmount;

    this.confirmationConfig = {
      title: '⚠️ Presupuesto Superado',
      message: `El monto ejecutado ($${executedAmount.toLocaleString('es-CO')}) supera el proyectado ($${projectedAmount.toLocaleString('es-CO')}) por $${difference.toLocaleString('es-CO')}.`,
      confirmText: 'Aceptar',
      cancelText: 'Cancelar',
      showCancel: true,
      type: 'warning',
      closable: false // Evita que se pueda cerrar clickeando fuera o con ESC
    };

    this.budgetAlertShown = true;
    this.showConfirmationModal = true;
  }

  /**
   * Confirmación para exceder presupuesto
   */
  async confirmExceedsBudget(amount: number, budget: number): Promise<boolean> {
    return await this.confirmationService.workOrder.exceedsBudget(amount, budget);
  }

  /**
   * Maneja la confirmación del modal
   */
  onConfirmationModalConfirmed(): void {
    if (this.budgetAlertShown) {
      this.closeBudgetAlert();
    } else {
      // Usar el método del componente base para manejar confirmaciones generales
      this.onModalConfirmed();
    }
  }

  /**
   * Maneja la cancelación del modal
   */
  onConfirmationModalCancelled(): void {
    if (this.budgetAlertShown) {
      this.closeBudgetAlert();
    } else {
      // Usar el método del componente base para manejar cancelaciones generales
      this.onModalCancelled();
    }
  }

  /**
   * Cierra el modal de alerta de presupuesto
   */
  private closeBudgetAlert(): void {
    this.budgetAlertShown = false; // Resetear para permitir futuras alertas
    this.showConfirmationModal = false;
  }

  /**
   * Maneja el cierre del modal
   */
  onConfirmationModalClosed(): void {
    if (this.budgetAlertShown) {
      this.closeBudgetAlert();
    } else {
      // Usar el método del componente base para manejar cierres generales
      this.onModalClosed();
    }
  }
}
